import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useProduct } from "@/context/ProductContext";
import { api, apiError } from "@/lib/api";

export default function Onboarding() {
  const product = useProduct();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [phone, setPhone] = useState("+1");
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/phone/list").then((r) => {
      if (r.data.some((p: any) => p.verified)) navigate("/simulator");
    });
  }, [navigate]);

  const addPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please agree to receive text messages to continue.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/phone/add", { e164_number: phone, consent });
      setDevCode(r.data.dev_code || null);
      setStep(2);
      toast.success("Verification code sent");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/phone/verify", { e164_number: phone, code });
      toast.success("Phone verified");
      navigate("/simulator");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-4">
        <Brand />
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-md px-5 pb-16">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          {step === 1 ? "Add your phone" : "Verify your phone"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {step === 1
            ? `${product.product_name} works by text. Add and verify your mobile number to begin.`
            : `Enter the 6-digit code we generated for ${phone}.`}
        </p>

        {step === 1 ? (
          <form onSubmit={addPhone} className="mt-8 space-y-5" data-testid="phone-form">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Mobile number (E.164)</Label>
              <Input id="phone" required value={phone} data-testid="phone-input"
                onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567" />
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-border bg-card p-4">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(Boolean(v))}
                data-testid="consent-checkbox" className="mt-0.5" />
              <span className="text-xs leading-relaxed text-muted-foreground" data-testid="consent-text">
                {product.consent_disclosure}
              </span>
            </label>
            <p className="text-[11px] text-muted-foreground">
              Terms and Privacy Policy are draft placeholders pending review.
            </p>

            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="phone-submit">
              {loading ? "Sending…" : "Send verification code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-8 space-y-5" data-testid="verify-form">
            {devCode && (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="dev-code-hint">
                <span className="font-medium text-primary">Dev only:</span> no real SMS in Phase 1A.
                Your code is <span className="font-mono font-semibold">{devCode}</span>.
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="code">Verification code</Label>
              <Input id="code" inputMode="numeric" required value={code} maxLength={6}
                data-testid="code-input" onChange={(e) => setCode(e.target.value)} placeholder="000000" />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="verify-submit">
              {loading ? "Verifying…" : "Verify & continue"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" className="inline-flex items-center gap-1 text-muted-foreground"
                onClick={() => setStep(1)} data-testid="back-to-phone">
                <ArrowLeft className="h-4 w-4" /> Change number
              </button>
              <button type="button" className="text-primary"
                onClick={async () => {
                  const r = await api.post("/phone/resend", { e164_number: phone });
                  setDevCode(r.data.dev_code || null);
                  toast.success("New code sent");
                }} data-testid="resend-code">
                Resend code
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4" /> Your number is only used to text with {product.product_name}.
        </div>
      </main>
    </div>
  );
}
